// Clientes — a simplified port of Comando Ops' Gestor de Clientes
// (src/ClientManager.js + src/ClientForm.js), scoped to the Desarrollos tenant.
//
// Kept from the original: the search-filter-in-useMemo shape, the initials
// based ID generator, the ID/phone duplicate pre-flight checks before INSERT,
// and the modal-over-list interaction.
// Dropped: the shipment-type tabs, xpress_id, the address book, the Excel
// migration trigger and its audit-log table, and hard delete. Desarrollos only
// needs name + phone + ID.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import { timeAgoEs } from './format';
import { Chip, EmptyText, ErrorText, PerfilChip, SkeletonRows } from './ui';

// Ported verbatim in spirit from ClientForm.generateClientId: initials first,
// then deeper characters, padded with digits, capped at 4 chars.
const generateClientId = (name) => {
  const parts = (name || '').split(' ').filter(Boolean);
  let id = '';
  for (let i = 0; i < parts.length && id.length < 4; i++) id += parts[i][0];

  let charIndex = 1;
  while (id.length < 4) {
    let added = false;
    for (let i = 0; i < parts.length && id.length < 4; i++) {
      if (parts[i].length > charIndex) {
        id += parts[i][charIndex];
        added = true;
      }
    }
    if (!added) break;
    charIndex++;
  }
  while (id.length > 0 && id.length < 4) id += '0';
  return id.substring(0, 4).toUpperCase();
};

const digitsOf = (s) => (s || '').replace(/\D/g, '');
const isValidPhone = (s) => digitsOf(s).length >= 8;

// ── Nuevo cliente ───────────────────────────────────────────────────────────
const NuevoClienteModal = ({ onClose, onCreated }) => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [clientId, setClientId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onNameChange = (value) => {
    setFullName(value);
    if (!idTouched) setClientId(generateClientId(value));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError('');

    if (!fullName.trim()) return setError('El nombre es obligatorio.');
    if (!isValidPhone(phone)) return setError('Ingresá un teléfono válido (mínimo 8 dígitos).');
    if (!clientId.trim()) return setError('El ID no puede quedar vacío.');

    setSaving(true);
    try {
      // Pre-flight duplicate checks, same as Gestor de Clientes.
      const [byId, byPhone] = await Promise.all([
        supabase
          .from('customers')
          .select('id')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('id', clientId.trim().toUpperCase())
          .is('deleted_at', null)
          .limit(1),
        supabase
          .from('customers')
          .select('id')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('phone_number', phone.trim())
          .is('deleted_at', null)
          .limit(1),
      ]);
      if (byId.error) throw byId.error;
      if (byPhone.error) throw byPhone.error;
      if (byId.data?.length) {
        setError('Ese ID ya está en uso. Elegí otro.');
        return;
      }
      if (byPhone.data?.length) {
        setError('Ese teléfono ya está registrado.');
        return;
      }

      // tenant_id is omitted on purpose: the column defaults to
      // current_tenant_id() and the INSERT policy re-checks it against the
      // operator's JWT, so the tenant guard stays in the database.
      const { error: insertError } = await supabase.from('customers').insert([
        {
          id: clientId.trim().toUpperCase(),
          full_name: fullName.trim(),
          phone_number: phone.trim(),
        },
      ]);
      if (insertError) throw insertError;

      onCreated();
    } catch (err) {
      console.error('[Desarrollos/Clientes] error creando cliente', err);
      setError('No pudimos guardar el cliente. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-slate-900">Nuevo cliente</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Nombre completo
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => onNameChange(e.target.value)}
              className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Teléfono
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+503 0000 0000"
              className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              ID de cliente
            </label>
            <input
              type="text"
              value={clientId}
              maxLength={4}
              onChange={(e) => {
                setIdTouched(true);
                setClientId(e.target.value.toUpperCase());
              }}
              className="w-full h-11 px-3 text-sm font-mono text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
            <p className="text-[10px] text-slate-400">
              Se genera con las iniciales del nombre. Podés cambiarlo.
            </p>
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full h-11 bg-[var(--brand-teal)] hover:brightness-[1.08] disabled:opacity-70 text-white font-semibold text-sm rounded-lg transition"
          >
            {saving ? 'Guardando…' : 'Crear cliente'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Detalle del cliente ─────────────────────────────────────────────────────
const ClienteDrawer = ({ cliente, onClose, onSaved }) => {
  const [fullName, setFullName] = useState(cliente.full_name || '');
  const [phone, setPhone] = useState(cliente.phone_number || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (saving) return;
    setError('');
    if (!fullName.trim()) return setError('El nombre es obligatorio.');
    if (!isValidPhone(phone)) return setError('Ingresá un teléfono válido (mínimo 8 dígitos).');

    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('customers')
        .update({ full_name: fullName.trim(), phone_number: phone.trim() })
        .eq('id', cliente.id)
        .eq('tenant_id', DESARROLLOS_TENANT_ID);
      if (updateError) throw updateError;
      onSaved();
    } catch (err) {
      console.error('[Desarrollos/Clientes] error actualizando cliente', err);
      setError('No pudimos guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <aside
        className="w-full max-w-md h-full bg-white border-l border-slate-200 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-900">Cliente {cliente.id}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Nombre completo
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Teléfono
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-11 bg-[var(--brand-teal)] hover:brightness-[1.08] disabled:opacity-70 text-white font-semibold text-sm rounded-lg transition"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>

          <div className="border-t border-slate-200 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Perfil Aduanero
            </p>
            <PerfilChip estado={cliente.perfil_estado} />
            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
              {cliente.perfil_estado
                ? 'La edición del perfil desde el panel de operador llega en el próximo brief.'
                : 'Este cliente todavía no completó su Perfil Aduanero.'}
            </p>
          </div>

          <div className="border-t border-slate-200 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Anexos
            </p>
            {cliente.anexos.length === 0 ? (
              <EmptyText>Este cliente todavía no tiene Anexos.</EmptyText>
            ) : (
              <div>
                {cliente.anexos.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0"
                  >
                    <Chip tone={a.status === 'live' ? 'blue' : 'green'}>
                      {a.status === 'live' ? 'En curso' : 'Completado'}
                    </Chip>
                    <p className="text-[10px] text-slate-400">{timeAgoEs(a.updated_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

// ── Lista principal ─────────────────────────────────────────────────────────
const ClientesPanel = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Three flat reads joined in JS. The perfil/anexo tables are keyed by
      // client_id, so a client-side join is cheap and avoids depending on
      // PostgREST embed resolution across two different foreign keys.
      const [customers, perfiles, anexos] = await Promise.all([
        supabase
          .from('customers')
          .select('id, full_name, phone_number, created_at')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('desarrollos_perfil_aduanero')
          .select('client_id, estado')
          .eq('tenant_id', DESARROLLOS_TENANT_ID),
        supabase
          .from('desarrollos_anexos')
          .select('id, client_id, status, updated_at')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .in('status', ['live', 'completed'])
          .order('updated_at', { ascending: false }),
      ]);
      if (customers.error) throw customers.error;
      if (perfiles.error) throw perfiles.error;
      if (anexos.error) throw anexos.error;

      const estadoById = Object.fromEntries(
        (perfiles.data || []).map((p) => [p.client_id, p.estado])
      );
      const anexosById = {};
      (anexos.data || []).forEach((a) => {
        (anexosById[a.client_id] = anexosById[a.client_id] || []).push(a);
      });

      setClientes(
        (customers.data || []).map((c) => {
          const list = anexosById[c.id] || [];
          return {
            ...c,
            perfil_estado: estadoById[c.id] || null,
            anexos: list,
            activos: list.filter((a) => a.status === 'live').length,
            ultima_actividad: list[0]?.updated_at || c.created_at,
          };
        })
      );
    } catch (err) {
      console.error('[Desarrollos/Clientes] error cargando clientes', err);
      setError('No pudimos cargar los clientes. Revisá tu conexión y volvé a intentar.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clientes;
    return clientes.filter(
      (c) =>
        (c.full_name || '').toLowerCase().includes(term) ||
        (c.phone_number || '').toLowerCase().includes(term)
    );
  }, [clientes, search]);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o teléfono…"
            className="w-full h-11 pl-9 pr-3 text-sm text-slate-900 bg-white border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
          />
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="h-11 px-4 bg-[var(--brand-teal)] hover:brightness-[1.08] text-white font-semibold text-sm rounded-lg transition shrink-0"
        >
          + Nuevo Cliente
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-5">
            <SkeletonRows rows={5} />
          </div>
        ) : error ? (
          <div className="px-5">
            <ErrorText>{error}</ErrorText>
          </div>
        ) : clientes.length === 0 ? (
          <div className="px-5">
            <EmptyText>
              Ningún cliente registrado. Creá el primero para empezar a generar Anexos.
            </EmptyText>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5">
            <EmptyText>Ningún cliente coincide con “{search}”.</EmptyText>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-slate-200">
                  {['ID', 'Nombre', 'Teléfono', 'Perfil', 'Anexos activos', 'Última actividad'].map((h) => (
                    <th
                      key={h}
                      className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{c.id}</td>
                    <td className="px-5 py-3 font-medium text-slate-800">{c.full_name || '—'}</td>
                    <td className="px-5 py-3 text-slate-500">{c.phone_number || '—'}</td>
                    <td className="px-5 py-3">
                      <PerfilChip estado={c.perfil_estado} />
                    </td>
                    <td className="px-5 py-3 text-slate-500">{c.activos}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">
                      {timeAgoEs(c.ultima_actividad)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <NuevoClienteModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}

      {selected && (
        <ClienteDrawer
          cliente={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </>
  );
};

export default ClientesPanel;
